// @ts-nocheck
/**
 * ドラフトシステム - ピック操作フック
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { DRAFT_CONFIG } from '../constants';

/**
 * ピック操作フック
 * @param {Object} session - ドラフトセッション
 * @param {Function} onPick - ピック実行コールバック
 * @returns {Object} ピック操作
 */
export const useDraftPick = (session, onPick) => {
  // ========================================
  // 状態
  // ========================================
  const [selectedCard, setSelectedCard] = useState(null);
  const [timeRemaining, setTimeRemaining] = useState(DRAFT_CONFIG.PICK_TIME_LIMIT);
  const [isAutoPickEnabled, setIsAutoPickEnabled] = useState(true);
  const timerRef = useRef(null);

  // ========================================
  // パック情報
  // ========================================

  /**
   * 現在のパックを取得
   */
  const currentPack = session?.draft?.packs?.player || [];

  /**
   * 現在のラウンド情報
   */
  const roundInfo = {
    pack: session?.draft?.currentPack || 1,
    round: session?.draft?.currentRound || 1,
    direction: session?.draft?.direction || 'clockwise',
    packSize: session?.config?.packSize || DRAFT_CONFIG.PACK_SIZE,
  };

  /**
   * 残りピック数
   */
  const remainingPicks = (() => {
    if (!session) return 0;
    const { config, draft } = session;
    const totalPerPack = config.packSize;
    const packsDone = draft.currentPack - 1;
    const roundsDone = draft.currentRound - 1;
    const totalDone = packsDone * totalPerPack + roundsDone;
    const total = config.packCount * totalPerPack;
    return total - totalDone;
  })();

  // ========================================
  // タイマー
  // ========================================

  /**
   * タイマーをリセット
   */
  const resetTimer = useCallback(() => {
    setTimeRemaining(DRAFT_CONFIG.PICK_TIME_LIMIT);
  }, []);

  /**
   * タイマー処理
   */
  useEffect(() => {
    // ピックフェーズでなければタイマー停止
    if (!session || session.status !== 'picking') {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      return;
    }

    // パックが空ならタイマー停止
    if (currentPack.length === 0) {
      return;
    }

    // タイマー開始
    timerRef.current = setInterval(() => {
      setTimeRemaining(prev => {
        if (prev <= 1) {
          // タイムアウト
          if (isAutoPickEnabled && currentPack.length > 0) {
            // 自動ピック（最初のカード）
            const autoPick = currentPack[0];
            if (autoPick && onPick) {
              setTimeout(() => onPick(autoPick.id), 0);
            }
          }
          return DRAFT_CONFIG.PICK_TIME_LIMIT;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [session, currentPack, isAutoPickEnabled, onPick]);

  /**
   * ラウンドが変わったらタイマーリセット
   */
  useEffect(() => {
    resetTimer();
    setSelectedCard(null);
  }, [session?.draft?.currentRound, session?.draft?.currentPack, resetTimer]);

  // ========================================
  // ピック操作
  // ========================================

  /**
   * カードを選択
   * @param {Object} card - 選択するカード
   */
  const selectCard = useCallback((card) => {
    setSelectedCard(card);
  }, []);

  /**
   * 選択をクリア
   */
  const clearSelection = useCallback(() => {
    setSelectedCard(null);
  }, []);

  /**
   * 選択したカードをピック
   */
  const confirmPick = useCallback(() => {
    if (!selectedCard || !onPick) return false;

    const success = onPick(selectedCard.id);
    if (success) {
      setSelectedCard(null);
      resetTimer();
    }
    return success;
  }, [selectedCard, onPick, resetTimer]);

  /**
   * カードを直接ピック（選択なしで即座に）
   * @param {string} cardId - ピックするカードID
   */
  const quickPick = useCallback((cardId) => {
    if (!onPick) return false;

    const success = onPick(cardId);
    if (success) {
      setSelectedCard(null);
      resetTimer();
    }
    return success;
  }, [onPick, resetTimer]);

  // ========================================
  // カード評価ヘルパー
  // ========================================

  /**
   * カードのレアリティランクを取得
   * @param {string} rarity - レアリティ
   * @returns {number} ランク（高いほど良い）
   */
  const getRarityRank = useCallback((rarity) => {
    const ranks = {
      C: 1, UC: 2, R: 3, SR: 4, UR: 5, HR: 6, SEC: 7, ALT: 8, SP: 9, GR: 10,
    };
    return ranks[rarity] || 1;
  }, []);

  /**
   * パック内のカードをレアリティでソート
   */
  const sortedByRarity = useCallback(() => {
    return [...currentPack].sort((a, b) => {
      return getRarityRank(b.rarity) - getRarityRank(a.rarity);
    });
  }, [currentPack, getRarityRank]);

  /**
   * パック内のカードをコストでソート
   */
  const sortedByCost = useCallback(() => {
    return [...currentPack].sort((a, b) => {
      return (a.cost || 0) - (b.cost || 0);
    });
  }, [currentPack]);

  /**
   * パック内のカードをタイプでグループ化
   */
  const groupedByType = useCallback(() => {
    const groups = {
      monster: [],
      magic: [],
      field: [],
      phase: [],
    };

    currentPack.forEach(card => {
      const type = card.type || 'monster';
      if (groups[type]) {
        groups[type].push(card);
      }
    });

    return groups;
  }, [currentPack]);

  // ========================================
  // 返却値
  // ========================================
  return {
    // 状態
    currentPack,
    selectedCard,
    timeRemaining,
    isAutoPickEnabled,
    roundInfo,
    remainingPicks,

    // 操作
    selectCard,
    clearSelection,
    confirmPick,
    quickPick,
    setIsAutoPickEnabled,
    resetTimer,

    // ヘルパー
    getRarityRank,
    sortedByRarity,
    sortedByCost,
    groupedByType,

    // 便利なプロパティ
    hasSelection: selectedCard !== null,
    isEmpty: currentPack.length === 0,
    packCount: currentPack.length,
  };
};

export default useDraftPick;
